import React from 'react'
import { useTranslation } from 'react-i18next'
import Toast from 'react-toast'
import { useTimer } from 'react-timer'
import { bewizrLearningTracksStore } from '~/stores'
import { observer } from '~/ui/component'
import { Panel, PushButton, TextBlock } from '~/ui/components'
import { SVGName } from '~/ui/components/SVG'
import { layout } from '~/ui/styling'

export interface Props {
  id: string
}

const BeWizrLearningTrackEnrollmentPanel = observer('BeWizrLearningTrackEnrollmentPanel', (props: Props) => {

  const {id} = props

  const [t] = useTranslation('bewizr-learning-tracks')

  const timer = useTimer()

  const document      = bewizrLearningTracksStore.learningTracks.document(id)
  const learningTrack = document.data

  const [working, setWorking] = React.useState<boolean>(false)

  const action = React.useMemo(() => {
    if (learningTrack?.isEnrolled) {
      return 'cancel'
    } else if (learningTrack?.isOnWaitingList) {
      return 'remove-from-waiting-list'
    } else if (learningTrack?.placesLeft === 0) {
      return 'enroll-for-waiting-list'
    } else {
      return 'enroll'
    }
  }, [learningTrack])

  const externalEnrollUrl = (action === 'enroll' || action === 'enroll-for-waiting-list')
    ? learningTrack?.links.externalEnrollUrl
    : null

  const update = React.useCallback(async () => {
    if (working) { return }

    setWorking(true)

    const result = await timer.await(
      action === 'cancel' || action === 'remove-from-waiting-list' ? document.cancel() : document.enroll(),
    )
    if (result?.status === 200) {
      Toast.show({
        type:   'success',
        title:  t(`enrollment.${action}.notification.title`, {learningTrack}),
        detail: t(`enrollment.${action}.notification.detail`, {learningTrack}),
        lifetime: 20_000,
      })
    }

    setWorking(false)
  }, [document, timer, working, t, learningTrack, action])

  //------
  // Rendering

  function render() {
    const icon: SVGName | undefined = action === 'cancel' || action === 'remove-from-waiting-list'
      ? 'cross' : action === 'enroll-for-waiting-list'
      ? 'clock' : undefined
    return (
      <Panel padding={layout.padding.inline.l} gap={layout.padding.m}>
        <TextBlock small>
          {t(`enrollment.${action}.summary`)}
        </TextBlock>
        {externalEnrollUrl != null ? (
          <PushButton
            icon='link'
            caption={t(`enrollment.${action}.caption`)}
            href={externalEnrollUrl}
            target='_blank'
          />
        ) : (
          <PushButton
            icon={icon}
            caption={t(`enrollment.${action}.caption`)}
            working={working}
            onTap={update}
          />
        )}
      </Panel>
    )
  }

  return render()

})

export default BeWizrLearningTrackEnrollmentPanel